import React, { useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { MenuItem } from "@mui/material";
import {
  IAM_CONTRACT,
  CLASSIFICATION_SENIORITY_LIST_TYPE,
  NON_CONTRACT,
  TWU_CONTRACT,
  EMPLOYEE_TEMP_LABEL,
  ADMIN_PDF_HEADER,
  EMPLOYEE_PDF_HEADER,
  WORKGROUP_MANAGER,
} from "../util/constants";
import { SENIORITY_MISSING_DATA } from "../util/constants";
import { CRITICAL_WARNING_FOR_SL_PDF } from "../util/messageConstants";
import { generateExportedFilename } from "../util/seniorityListUtils";
import WarningDialog from "./fundamentals/WarningDialog";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";

const SeniorityListPdfExport = ({
  seniorityList,
  contract,
  seniorityListType,
  station,
  generatedTimestamp,
  workgroup,
  criticalMissingDataCount,
  onExportMenuClose,
}) => {
  const [showCriticalErrorWarning, setShowCriticalErrorWarning] = useState(false);

  const columns = [];
  const baseColumns = [
    { title: "Rank", field: "rank" },
    { title: "EID", field: "eid" },
    { title: "Name", field: `shortName` },
    { title: "Phone #", field: "phoneNumber" },
    { title: "FT/PT", field: `ftpt` },
  ];
  const workgroupBasedColumns = [];

  if (workgroup === WORKGROUP_MANAGER) {
    workgroupBasedColumns.push({
      title: "Job Title",
      field: "jobTitle",
    });
  }

  let contractBasedColumns = [];
  if (contract === IAM_CONTRACT) {
    if (seniorityListType === CLASSIFICATION_SENIORITY_LIST_TYPE) {
      contractBasedColumns.push(
        ...[
          {
            title: "Classification Date",
            field: "classificationDate",
          },
          {
            field: "contractDate",
            title: "Contract Date",
          },
          {
            field: "hireDate",
            title: "Hire Date",
          },
        ]
      );
    } else {
      contractBasedColumns.push(
        ...[
          {
            field: "contractDate",
            title: "Contract Date",
          },
          {
            field: "classificationDate",
            title: "Classification Date",
          },
          {
            field: "hireDate",
            title: "Hire Date",
          },
        ]
      );
    }
  } else if (contract === TWU_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "wnSliSeniorityDate",
          title: "WN SLI Seniority Date",
        },
        {
          field: "flAtlSeniorityDate",
          title: "FL ATL Seniority Date",
        },
        {
          field: "dualSeniorityDate",
          title: "Dual Seniority Date",
        },
        {
          field: "hireDate",
          title: "Hire Date",
        },
      ]
    );
  } else if (contract === NON_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "seniorityDate",
          title: "Seniority Date",
        },
        {
          field: "hireDate",
          title: "Hire Date",
        },
      ]
    );
  } else {
    console.log("error unrecognized contract: ", contract);
  }

  columns.push(...baseColumns);
  columns.push(...workgroupBasedColumns);
  columns.push(...contractBasedColumns);

  const downloadPdf = (pdfHeader) => {
    const doc = new jsPDF("p", "mm");
    const isAdmin = pdfHeader === ADMIN_PDF_HEADER;
    const currentDateTime = getStationTimeZoneDateTimeStringFromUTCInteger(generatedTimestamp, station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );

    const totalPagesExp = "{total_pages_count_string}";
    const exclusionList = ["phoneNumber"];
    const employeeColumns = columns.filter((item) => !exclusionList.includes(item.field));
    doc.autoTable({
      theme: "grid",
      bodyStyles: { lineColor: "#000000" },
      styles: { fontSize: 9, cellPadding: { top: 0, right: 0, bottom: 0, left: 0.4 } },
      didParseCell: (seniorityList) => {
        if (seniorityList.section === "body") {
          switch (seniorityList.column.dataKey) {
            case "shortName":
              seniorityList.cell.text =
                seniorityList.row.raw.isTemp && seniorityList.row.raw.homeStation !== station
                  ? `${seniorityList.row.raw.lastName}, ${seniorityList.row.raw.shortName} (${EMPLOYEE_TEMP_LABEL})`
                  : `${seniorityList.row.raw.lastName}, ${seniorityList.row.raw.shortName}`;
              break;
            case "phoneNumber":
              seniorityList.cell.text =
                seniorityList.row.raw.phoneNumber === "" ? "-" : seniorityList.row.raw.phoneNumber;
              break;
            case "contractDate":
              seniorityList.cell.text =
                seniorityList.row.raw.contractDate === SENIORITY_MISSING_DATA
                  ? "-"
                  : seniorityList.row.raw.contractDate;
              break;
            case "classificationDate":
              seniorityList.cell.text =
                seniorityList.row.raw.classificationDate === SENIORITY_MISSING_DATA
                  ? "-"
                  : seniorityList.row.raw.classificationDate;
              break;
            case "hireDate":
              seniorityList.cell.text =
                seniorityList.row.raw.hireDate === SENIORITY_MISSING_DATA ? "-" : seniorityList.row.raw.hireDate;
              break;
            case "wnSliSeniorityDate":
              seniorityList.cell.text =
                seniorityList.row.raw.wnSliSeniorityDate === SENIORITY_MISSING_DATA
                  ? "-"
                  : seniorityList.row.raw.wnSliSeniorityDate;
              break;
            case "flAtlSeniorityDate":
              seniorityList.cell.text =
                seniorityList.row.raw.flAtlSeniorityDate === SENIORITY_MISSING_DATA ||
                seniorityList.row.raw.flAtlSeniorityDate === ""
                  ? "-"
                  : seniorityList.row.raw.flAtlSeniorityDate;
              break;
            case "seniorityDate":
              seniorityList.cell.text =
                seniorityList.row.raw.seniorityDate === SENIORITY_MISSING_DATA
                  ? "-"
                  : seniorityList.row.raw.seniorityDate;
              break;
            case "dualSeniorityDate":
              seniorityList.cell.text =
                seniorityList.row.raw.dualSeniorityDate === SENIORITY_MISSING_DATA
                  ? "-"
                  : seniorityList.row.raw.dualSeniorityDate;
              break;
            default:
              break;
          }
        }
      },
      headStyles: {
        fillColor: "#e6e7e8",
        textColor: "#000000",
        lineColor: "#000000",
        lineWidth: 0.1,
      },
      columns: isAdmin
        ? columns && columns.map((col) => ({ ...col, dataKey: col.field }))
        : employeeColumns && employeeColumns.map((col) => ({ ...col, dataKey: col.field })),
      body: seniorityList,
      margin: { top: isAdmin ? 45 : 35 },
      didDrawPage: function (data) {
        // Header
        doc.setFontSize(16);
        doc.setTextColor(40);
        if (isAdmin) {
          doc.text(`Seniority List [${pdfHeader}]`, 14, 10);
        } else {
          doc.text("Seniority List", 14, 10);
        }
        doc.setFontSize(10);
        if (isAdmin) {
          doc.setFillColor("#e6e7e8");
          doc.rect(14, 15, 182, 7, "F");
          doc.setTextColor("#000000");
          doc.text(19, 20, "Please do not post this, use the Employee View PDF export for posting.");
        }
        doc.setFillColor("#ebeced");
        doc.rect(14, isAdmin ? 25 : 15, 182, 15, "F");
        doc.setTextColor("#8f8f8f");
        doc.text("STATION", 17, isAdmin ? 30 : 20);
        doc.text("WORK GROUP ", 34, isAdmin ? 30 : 20);
        doc.text("SENIORITY LIST TYPE", 63, isAdmin ? 30 : 20);
        doc.text("LAST GENERATED", 105, isAdmin ? 30 : 20);
        doc.text("TOTAL EMPLOYEES", 142, isAdmin ? 30 : 20);
        doc.text("FT / PT", 180, isAdmin ? 30 : 20);
        doc.setTextColor("#000000");
        doc.text(`${station}`, 20, isAdmin ? 35 : 25);
        doc.text(`${workgroup}`, 34, isAdmin ? 35 : 25);
        doc.text(`${seniorityListType}`, 69, isAdmin ? 35 : 25);
        doc.text(currentDateTime, 104, isAdmin ? 35 : 25);
        doc.text(`${seniorityList.filter((item) => item).length}`, 155, isAdmin ? 35 : 25);
        doc.text(
          `${seniorityList.filter((item) => item.ftpt === "FT").length} / ${
            seniorityList.filter((item) => item.ftpt === "PT").length
          }`,
          180,
          isAdmin ? 35 : 25
        );
        doc.setFontSize(8);
        //footer
        var currentpage = 0;
        if (currentpage < data.pageCount) {
          doc.text(
            data.pageCount + " of " + totalPagesExp,
            doc.internal.pageSize.getWidth() - 21,
            doc.internal.pageSize.height - 10
          );
          currentpage = data.pageCount;
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }

    const fileName = generateExportedFilename({
      station,
      workgroup,
      seniorityListType,
      generatedTimestamp,
      isAdmin,
      fileType: "pdf",
    });

    doc.save(fileName);
  };

  const handlePDFMenuItemClick = (pdfHeader) => {
    if (criticalMissingDataCount > 0) {
      setShowCriticalErrorWarning(true);
    } else {
      try {
        downloadPdf(pdfHeader);
        onExportMenuClose();
      } catch (e) {
        console.log("Error during downloading pdf!");
      }
    }
  };

  const handleDialogClose = () => setShowCriticalErrorWarning(false);

  return (
    <>
      <MenuItem
        onClick={() => {
          handlePDFMenuItemClick(ADMIN_PDF_HEADER);
        }}
      >
        Admin PDF
      </MenuItem>
      <MenuItem
        onClick={() => {
          handlePDFMenuItemClick(EMPLOYEE_PDF_HEADER);
        }}
      >
        Employee View PDF
      </MenuItem>
      <WarningDialog
        isDialogOpen={showCriticalErrorWarning}
        handleClickDialogClose={handleDialogClose}
        header="Warning"
        message={CRITICAL_WARNING_FOR_SL_PDF}
        severity={"error"}
      />
    </>
  );
};

export default SeniorityListPdfExport;
